import axios, { type AxiosInstance } from 'axios'
import { environment } from './environment'
import { createPasskeyClient } from '@valiant/ui-shared'
export class Auth {
  private readonly passkeys = createPasskeyClient(environment.API_URL, true)
  passkeysEnabled = () => this.passkeys.enabled()
  passkeyLoginAvailable = (username: string, password: string) => this.passkeys.loginAvailable(username, password)
  loginWithPasskey = (username: string, password: string) => this.passkeys.login(username, password)
  private readonly axiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: environment.API_URL,
      headers: {
        Accept: 'application/json',
      },
    })

    this.axiosInstance.interceptors.request.use(
      (config) => {
        return config
      },
      (error) => {
        console.error('Login request failed')
        const errorMessage =
          error.response?.data != null
            ? error.response.data
            : (error.message ?? 'Unknown error occurred')

        // Throw the error message instead of the entire error object
        return Promise.reject(new Error(errorMessage))
      },
    )

    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response
      },
      (error) => {
        console.error('Login response failed')
        const errorMessage =
          error.response?.data != null
            ? error.response.data
            : (error.message ?? 'Unknown error occurred')
        // Throw the error message instead of the entire error object
        return Promise.reject(new Error(errorMessage))
      },
    )
  }

  async login(
    username: string,
    password: string,
    secret?: string,
  ): Promise<{ token: string; refreshToken: string }> {
    const response = await this.axiosInstance.post<{
      token: string
      refreshToken: string
    }>('/authentication/admin/login', { username, password, secret })
    return response.data
  }

  isJwtStale(jwt: string): boolean {
    const payload = JSON.parse(atob(jwt.split('.')[1]))
    const expiration = payload.exp * 1000
    return Date.now() >= expiration
  }

  async refresh(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
    const response = await this.axiosInstance.post<{
      token: string
      refreshToken: string
    }>('/authentication/admin/refreshtoken', { refreshToken })
    return response.data
  }

  async resetPassword(password: string, token: string): Promise<void> {
    await this.axiosInstance.post('/authentication/admin/resetpassword', { password, token })
  }

  async requestMFA(username: string, password: string): Promise<string> {
    const response = await this.axiosInstance.post<{ secret: string }>(
      '/authentication/admin/request-mfa',
      { username, password },
    )
    return response.data.secret
  }

  async setupMFA(username: string, password: string, secret: string): Promise<void> {
    await this.axiosInstance.post('/authentication/admin/setup-mfa', {
      username,
      password,
      secret,
    })
  }
}
